import type { UserSettings } from "./domain";
export function chooseVoice(
  voices: SpeechSynthesisVoice[],
  lang: string,
  offline: boolean,
) {
  return (
    voices.find((v) => v.lang === lang && v.localService) ??
    (!offline
      ? (voices.find((v) => v.lang === lang) ??
        voices.find((v) => v.lang.startsWith("en")))
      : undefined)
  );
}
export function speak(
  text: string,
  settings: UserSettings,
  onStatus: (s: string) => void,
) {
  if (!("speechSynthesis" in window)) {
    onStatus(
      "Bu tarayıcı seslendirmeyi desteklemiyor. Cümleyi okuyarak çalışabilirsin.",
    );
    return false;
  }
  const voices = speechSynthesis.getVoices(),
    voice = chooseVoice(voices, settings.voice, !navigator.onLine);
  // Only device-reported local voices are used. Personal sentences are not sent to remote voice services.
  if (!voice?.localService) {
    onStatus(
      "Cihazda kullanılabilir yerel İngilizce ses bulunamadı. iPhone konuşma ayarlarından İngilizce ses indirip yeniden dene.",
    );
    return false;
  }
  speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = settings.voice;
  u.voice = voice;
  u.rate = settings.speechRate;
  u.onerror = () =>
    onStatus(
      "Ses çalınamadı. Cihazın İngilizce sesini kontrol et veya okuyarak devam et.",
    );
  u.onstart = () => onStatus("Dinleniyor. " + voice.name);
  u.onend = () => onStatus("Seslendirme tamamlandı.");
  speechSynthesis.speak(u);
  return true;
}
export function speechAvailable(settings: UserSettings) {
  return (
    "speechSynthesis" in window &&
    speechSynthesis.getVoices().some((v) => v.lang === settings.voice && v.localService)
  );
}
export async function installOffline(
  onStatus: (s: string) => void,
  onUpdate: (r: ServiceWorkerRegistration) => void,
) {
  if (!("serviceWorker" in navigator) || !import.meta.env.PROD) {
    onStatus("Çevrimdışı hazırlık üretim sürümünde etkin.");
    return;
  }
  try {
    const r = await navigator.serviceWorker.register(
      new URL("sw.js", document.baseURI),
      { scope: "./" },
    );
    function ready() {
      onStatus("Çevrimdışı kullanıma hazır");
    }
    if (r.active) ready();
    if (r.waiting) onUpdate(r);
    r.addEventListener("updatefound", () => {
      const w = r.installing;
      w?.addEventListener("statechange", () => {
        if (w.state === "installed") {
          if (navigator.serviceWorker.controller) onUpdate(r);
          else ready();
        }
        if (w.state === "redundant")
          onStatus(
            "Çevrimdışı hazırlık tamamlanamadı. İnternet açıkken yeniden yükle.",
          );
      });
    });
    navigator.serviceWorker.ready.then(ready);
  } catch {
    onStatus(
      "Çevrimdışı hazırlık başarısız. HTTPS bağlantısını kontrol edip yeniden yükle.",
    );
  }
}
export function applyTheme(s: UserSettings) {
  const dark =
    s.theme === "dark" ||
    (s.theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
  document.documentElement.dataset.motion = s.motion ? "on" : "off";
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? "#11151c" : "#f5f6f8");
}
